<template>
	<section id="team">
		<template v-if="teamMembers.length > 0">
			<!-- API currently broken for this use case
			<div class="team-master-action-bar" v-if="activeTenantId === 'master'">
				<v-btn secondary
					class="team-master-view-all-users"
					@click="toggleViewAllUsers"
				>
					<template v-if="showAllTenantUsers">
						Show Only Quavo Users
					</template>
					<template v-else>
						Show All Users by Tenant
					</template>
				</v-btn>
			</div>
			-->
			<div class="team-table-toolbar">
				<v-btn @click="addUser()" class="add-user-button secondary">Add a User</v-btn>
				<v-text-field
					class="team-table-search"
					v-model="search"
					hide-details
					single-line
					prepend-icon="mdi-magnify"
					placeholder="email search"
				></v-text-field>
			</div>

			<div>
				<v-data-table
					:headers="headers"
					:items="teamMembers"
					item-key="userId"
					:search="search"
					:items-per-page="10"
					:mobile-breakpoint="1025"
					:hide-default-footer="teamMembers.length < 10"
					:group-by="userGrouping"
				>
					<template v-slot:item="{ item }">
						<tr :class="[{'disabled-user' : item.isDisabled}, 'user-row']">
							<td class="user-item-key" @click="onViewProfile(item.userId)">{{ item.emailAddress }}</td>
							<td class="user-item-role">{{ item.accessRole }}</td>
							<template v-if="hasClientOptions">
								<template v-if="item.isAllowedAllClients">
									<td class="user-item-client">
										<div>All Clients</div>
									</td>
								</template>
								<template v-else>
									<td class="user-item-client">
										<div v-for="(client, index) in item.allowedClients" :key="index">{{ client }}</div>
									</td>
								</template>
							</template>
							<td class="row-actions user-item-action">
								<v-btn text @click="onViewProfile(item.userId)" class="team-view-profile-button">
									View Profile
									<v-icon>mdi-chevron-right</v-icon>
								</v-btn>
							</td>
						</tr>
					</template>
				</v-data-table>
			</div>
		</template>
		<template v-else-if="teamMembersError">
			<template v-if="tenantId === 'master'">
				<v-alert
					class="warning"
					border="left"
					icon="mdi-alert-octagon"
					colored-border
				>
					The user list failed or came back empty. If this is a new tenant, add a user, otherwise contact the dev team.
				</v-alert>
				<div class="team-master-view-empty">
					<v-btn @click="addUser()" class="add-user-button secondary">Add a User</v-btn>
				</div>
			</template>
			<template v-else>
				<v-alert
					class="error"
					border="left"
					icon="mdi-alert-octagon"
					colored-border
				>
					We were not able to load your team info. Try logging off and back on. If the problem persists, please contact support.
				</v-alert>
			</template>
		</template>
		<template v-else>
			<v-skeleton-loader type="table-heading"></v-skeleton-loader>
			<v-skeleton-loader type="table-head"></v-skeleton-loader>
			<v-skeleton-loader type="table-tbody"></v-skeleton-loader>
		</template>
	</section>
</template>

<script>
	/**
	 * @fileoverview team.vue displays a list of team members based on the active tenantId.
	 * It relies on vuex to map the state variables from the store files.
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from 'vuex';

	export default {
		name: "team",
		props: {
			/**
			 * Flag for client options
			 */
			hasClientOptions: {
				Type: Boolean,
				Default: false
			},
		},
		data() {
			return {
				search: "",	// search string
				/**
				 * Headers for the team members data table
				 */
				headers: [
					{
						text: "Email",
						align: "start",
						groupable: false,
						value: "emailAddress",
					},
					{
						text: "Role",
						groupable: false,
						value: "accessRole",
					},
					{
						text: "",
						value: "actions",
						groupable: false,
					},
				],
			};
		},
		computed: {
			...mapState({
				teamMembersError: (state) => state.account.teamMembersError,
				inactiveTeamMembers: (state) => state.account.inactiveTeamMembers,
				activeTeamMembers: (state) => state.account.activeTeamMembers,
				activeTenantId: (state) => state.account.activeTenantId,
				tenantId: (state) => state.account.tenantId,
				tenantList: (state) => state.account.tenantList,
				showAllTenantUsers: (state) => state.account.showAllTenantUsers,
			}),
			/**
			 * Get the active and inactive team members of the user tenant
			 * @returns array of team member objects
			 */
			teamMembers() {
				let team = [];

				this.activeTeamMembers.map(member => {
					if (this.showAllTenantUsers){
						member = this.determineTenant(member);
					}
					team.push(member);
				})
				this.inactiveTeamMembers.map(member => {
					if (this.showAllTenantUsers){
						member = this.determineTenant(member);
					}
					team.push(member);
				})
				return team;
			},
			/**
			 * Set the string for the group-by v-data-table parameter
			 * @returns "Tenant" string if showAllTenantUsers is true, null if false
			 */
			userGrouping() {
				return this.showAllTenantUsers ? "Tenant" : null;
			}
		},
		methods: {
			/**
			 * Determine the tenant for displaying all users in the admin view
			 * @param {Object} userObj - team member
			 * @returns user object with updated tenant label
			 */
			determineTenant(userObj) {
				let tenantLabel = '';
				let tenantObj = this.tenantList.find( element => element.tenantId === userObj.tenantId);

				// don't mess with master, just set it manually
				if (userObj.tenantId === 'master') {
					tenantLabel = "Quavo";
				}
				// if you found something in the list, let's use it
				else if (tenantObj) {
					tenantLabel = tenantObj.tenantLabel ? tenantObj.tenantLabel : '';
				}
				// otherwise we're in a weird space... let's call that out
				else {
					tenantLabel = "Unknown";
				}

				userObj.Tenant = tenantLabel;
				return userObj;
			},
			/**
			 * View a user's profile
			 * @param {String} userId - id for the user we're viewing
			 */
			onViewProfile(userId) {
				this.$emit("viewProfile", userId);
			},
			/**
			 * Toggle showAllTenantUsers
			 */
			toggleViewAllUsers() {
				let toggleVal = !this.showAllTenantUsers;
				this.$store.dispatch("clearTeamMembers");
				this.$store.dispatch("setShowAllTenantUsers", toggleVal);
				
				if (this.showAllTenantUsers) {
					// exclude tenant bounds
					this.$store.dispatch("setTeamMembers");
				} else {
					this.$store.dispatch("setTeamMembers", 'master');
				}
			},
			/**
			 * Emit addUser to the parent
			 */
			addUser() {
				this.$emit("addUser");
			}
		},
	};
</script>
<!-- Styles defined in _team.scss -->
